import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  useColorModeValue,
  HStack,
  Icon,
  VStack,
  Heading
} from '@chakra-ui/react';
import { FiAlertCircle, FiDollarSign, FiFileText } from 'react-icons/fi';
import { useNavigate, useOutletContext } from 'react-router-dom';

const mock835Data = [
  {
    id: 'RMT-1001',
    patient: 'Sofia Ramirez',
    dos: '2026-07-08',
    payer: 'Keystone First',
    billed: '$186.00',
    paid: '$0.00',
    status: 'denied',
    tier: 'Tier B',
    issue: 'CARC 50: Medical Necessity',
    action: 'Review LLM Fix',
    route: '/fq-pa/demo/medical-necessity'
  },
  {
    id: 'RMT-1002',
    patient: 'Jerome Baker',
    dos: '2026-07-09',
    payer: 'Keystone First',
    billed: '$142.00',
    paid: '$0.00',
    status: 'denied',
    tier: 'Tier B',
    issue: 'CARC 97: Bundled (Same-Day Dental + Medical)',
    action: 'Review Appeal \u2014 PA Allows Same-Day',
    route: '/fq-pa/demo/same-day-restraint'
  },
  {
    id: 'RMT-1003',
    patient: 'Linda Martinez',
    dos: '2026-07-10',
    payer: 'UPMC for You',
    billed: '$186.00',
    paid: '$0.00',
    status: 'denied',
    tier: 'Tier B',
    issue: 'CARC 16: Lacks Information',
    action: 'Review Appeal Draft',
    route: '/fq-pa/demo/appeal-draft'
  },
  {
    id: 'RMT-1004',
    patient: 'Walter Brooks',
    dos: '2026-07-10',
    payer: 'Keystone 65 HMO (IBC Medicare Advantage)',
    billed: '$198.42',
    paid: '$89.00',
    status: 'underpaid',
    tier: 'Tier A',
    issue: 'Missed MA Wrap (Rev 0519)',
    action: 'Auto-Generate Wrap',
    route: null,
    wrapDemo: true
  }
];

const WRAP_CLAIM = {
  type: '837I',
  wrap_scenario: true,
  patient: { first_name: 'Walter', last_name: 'Brooks', member_id: 'IBX55H2274901' },
  provider: { name: 'Amara Okafor, CRNP' },
  claim: {
    payer: 'Keystone 65 HMO (IBC Medicare Advantage)',
    payer_class: 'Medicare Advantage',
    diagnoses: ['E11.65', 'I50.32'],
    procedures: [{ cpt: 'G0467', modifiers: [], charge: 198.42 }]
  },
  rules_fired: [
    {
      rule_name: 'Medicare Advantage Wrap Detection',
      description: 'MA plan payment ($89.00) is below the Medicare FQHC PPS rate ($198.42). Dual-eligible (QMB) \u2014 beneficiary cost-sharing is $0 and may not be billed.',
      action: 'Generate supplemental wrap claim with revenue code 0519 to Novitas (JL). If rate not on file, claim returns with reason code 37098 \u2014 submit MA contract documentation.',
      citation: 'CMS FQHC PPS FAQ; Medicare Claims Processing Manual, Ch. 9'
    }
  ],
  wraparound: { pps_rate: 198.42, mcp_paid: 89.00, wraparound_owed: 109.42 }
};

const FqPa835ReviewQueue = () => {
  const { selectedClient } = useOutletContext();
  const navigate = useNavigate();
  const [queue, setQueue] = useState(mock835Data);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const getStatusBadge = (status) => {
    switch (status) {
      case 'denied':
        return <Badge colorScheme="red" p={1} borderRadius="md"><HStack spacing={1}><Icon as={FiAlertCircle} /><Text>Denied</Text></HStack></Badge>;
      case 'underpaid':
        return <Badge colorScheme="orange" p={1} borderRadius="md"><HStack spacing={1}><Icon as={FiDollarSign} /><Text>Missed Wrap</Text></HStack></Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'Tier A':
        return <Badge colorScheme="blue" p={1} borderRadius="md">Tier A (Review)</Badge>;
      case 'Tier B':
        return <Badge colorScheme="purple" p={1} borderRadius="md">Tier B (LLM)</Badge>;
      case 'Tier C':
        return <Badge colorScheme="red" p={1} borderRadius="md">Tier C (Manual)</Badge>;
      default:
        return <Badge>{tier}</Badge>;
    }
  };

  const handleReviewClick = (claim) => {
    if (claim.route) {
      navigate(claim.route);
    } else {
      // Wrap row: seed the claim-detail view with the dual-eligible wrap scenario
      if (claim.wrapDemo) {
        sessionStorage.setItem('mockClaimData', JSON.stringify(WRAP_CLAIM));
      }
      navigate('/fq-pa/claim/demo');
    }
  };

  return (
    <Box maxW="7xl" mx="auto">
      <Flex justify="space-between" align="center" mb="6">
        <Box>
          <Heading size="lg" color="blue.700">835 Remittance Review</Heading>
          <Text color="gray.500" mt={1}>
            Actionable denials and underpayments from the overnight clearinghouse batch.
          </Text>
        </Box>
      </Flex>

      <Box bg={bgColor} shadow="sm" borderRadius="lg" borderWidth="1px" borderColor={borderColor} overflow="hidden">
        <Box p="4" borderBottomWidth="1px" borderColor={borderColor} bg={headerBg}>
          <HStack spacing={6}>
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" color="gray.500">Total Actionable</Text>
              <Text fontSize="xl" fontWeight="bold">{queue.length}</Text>
            </VStack>
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" color="gray.500">Wraparound Opportunities</Text>
              <Text fontSize="xl" fontWeight="bold" color="orange.500">{queue.filter(q => q.status === 'underpaid').length}</Text>
            </VStack>
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" color="gray.500">Hard Denials</Text>
              <Text fontSize="xl" fontWeight="bold" color="red.500">{queue.filter(q => q.status === 'denied').length}</Text>
            </VStack>
          </HStack>
        </Box>

        <Table variant="simple">
          <Thead bg={headerBg}>
            <Tr>
              <Th>Remit ID</Th>
              <Th>Patient / DOS</Th>
              <Th>Payer</Th>
              <Th>Status</Th>
              <Th>Resolution Tier</Th>
              <Th>Issue</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.map((claim) => (
              <Tr key={claim.id} _hover={{ bg: hoverBg }}>
                <Td fontWeight="medium">{claim.id}</Td>
                <Td>
                  <Text fontWeight="bold">{claim.patient}</Text>
                  <Text fontSize="sm" color="gray.500">{claim.dos}</Text>
                </Td>
                <Td><Badge variant="outline" colorScheme="gray">{claim.payer}</Badge></Td>
                <Td>{getStatusBadge(claim.status)}</Td>
                <Td>{getTierBadge(claim.tier)}</Td>
                <Td>
                  <Text fontSize="sm" fontWeight="bold" color={claim.status === 'denied' ? 'red.600' : 'orange.600'}>
                    {claim.issue}
                  </Text>
                </Td>
                <Td>
                  <Button 
                    size="sm" 
                    colorScheme={claim.tier === 'Tier B' ? 'purple' : (claim.status === 'denied' ? 'blue' : 'green')} 
                    variant={claim.tier === 'Tier B' ? 'solid' : 'outline'} 
                    leftIcon={claim.status === 'denied' ? <FiFileText /> : <FiDollarSign />}
                    onClick={() => handleReviewClick(claim)}
                  >
                    {claim.action}
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default FqPa835ReviewQueue;